import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { useCallback, useEffect, useImperativeHandle, forwardRef } from "react";
import { Button } from "./ui/button";

export interface EditorHandle {
  appendText: (text: string) => void;
  replaceContent: (html: string) => void;
  getText: () => string;
  getHTML: () => string;
}

interface DictationEditorProps {
  content?: string;
  onUpdate?: (html: string, text: string) => void;
}

export const DictationEditor = forwardRef<EditorHandle, DictationEditorProps>(
  function DictationEditor({ content, onUpdate }, ref) {
    const editor = useEditor({
      immediatelyRender: false,
      extensions: [
        StarterKit,
        Placeholder.configure({
          placeholder: "Start dictating or type your SOP content here...",
        }),
      ],
      content: content || "",
      onUpdate: ({ editor }) => {
        onUpdate?.(editor.getHTML(), editor.getText());
      },
      editorProps: {
        attributes: {
          class:
            "prose prose-sm dark:prose-invert max-w-none min-h-[200px] sm:min-h-[400px] p-3 sm:p-4 focus:outline-none",
        },
      },
    });

    useImperativeHandle(
      ref,
      () => ({
        appendText: (text: string) => {
          if (!editor) return;
          const currentContent = editor.getText();
          const separator = currentContent.trim() ? "\n\n" : "";
          editor
            .chain()
            .focus()
            .insertContentAt(editor.state.doc.content.size - 1, separator + text)
            .run();
        },
        replaceContent: (html: string) => {
          if (!editor || editor.isDestroyed) return;
          editor.commands.setContent(html);
        },
        getText: () => editor?.getText() ?? "",
        getHTML: () => editor?.getHTML() ?? "",
      }),
      [editor]
    );

    // Update editor content when prop changes (async draft load)
    useEffect(() => {
      if (editor && content && !editor.isDestroyed) {
        if (!editor.getText().trim()) {
          editor.commands.setContent(content);
        }
      }
    }, [editor, content]);

    if (!editor) return null;

    return (
      <div className="border rounded-lg overflow-hidden bg-card">
        <div className="flex flex-wrap gap-1 p-1.5 sm:p-2 border-b bg-muted/50">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 ${editor.isActive("bold") ? "bg-accent" : ""}`}
          >
            <strong>B</strong>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 ${editor.isActive("italic") ? "bg-accent" : ""}`}
          >
            <em>I</em>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            className={`min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 ${editor.isActive("heading", { level: 2 }) ? "bg-accent" : ""}`}
          >
            H2
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
            className={`min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 ${editor.isActive("heading", { level: 3 }) ? "bg-accent" : ""}`}
          >
            H3
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 ${editor.isActive("bulletList") ? "bg-accent" : ""}`}
          >
            List
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 ${editor.isActive("orderedList") ? "bg-accent" : ""}`}
          >
            1.2.3
          </Button>
        </div>
        <EditorContent editor={editor} />
      </div>
    );
  }
);
